import React, { useState } from "react";
import { AiFillGithub, AiOutlineTwitter } from "react-icons/ai";
import {
  FaLinkedinIn, FaLinux, FaAward, FaPhp,
  FaMapMarkerAlt, FaBriefcase, FaGraduationCap,
} from "react-icons/fa";
import { CgCPlusPlus } from "react-icons/cg";
import { DiJavascript1, DiReact, DiNodejs, DiMongodb, DiGit, DiPython } from "react-icons/di";
import {
  SiExpress, SiNextdotjs, SiTailwindcss, SiBootstrap,
  SiDocker, SiRedux, SiHtml5, SiCss3, SiTypescript, SiPostgresql,
  SiVisualstudiocode, SiPostman, SiVercel, SiNetlify, SiGithub,
  SiNpm, SiFigma, SiMysql, SiSocketdotio, SiJsonwebtokens,
  SiRender, SiFirebase, SiMui, SiLeetcode,
} from "react-icons/si";
import { TbBinaryTree } from "react-icons/tb";
import {
  BsRocketTakeoffFill, BsCodeSlash, BsAwardFill, BsCalendarCheck,
} from "react-icons/bs";
import {
  MdOutlineWeb, MdOutlinePsychology, MdOutlineGroupWork,
} from "react-icons/md";

/* ─────────────────────────────────────────
   STATS
───────────────────────────────────────── */
const STATS = [
  { num: "15+", label: "Projects Built", Icon: BsRocketTakeoffFill, color: "#6366f1" },
  { num: "2+",  label: "Years Coding",   Icon: BsCalendarCheck,     color: "#10b981" },
  { num: "25+", label: "Technologies",   Icon: BsCodeSlash,         color: "#f59e0b" },
  { num: "12+", label: "Certificates",   Icon: BsAwardFill,         color: "#ec4899" },
];

/* ─────────────────────────────────────────
   FOCUS AREAS — all icons from react-icons
───────────────────────────────────────── */
const FOCUS_AREAS = [
  {
    Icon: MdOutlineWeb,
    title: "Full-Stack Engineering",
    desc: "End-to-end web apps — from pixel-perfect UIs to scalable backend APIs and databases.",
    color: "#6366f1",
  },
  {
    Icon: MdOutlinePsychology,
    title: "AI Engineering",
    desc: "Learning to build LLM-powered products, AI integrations, and intelligent systems.",
    color: "#f59e0b",
  },
  {
    Icon: MdOutlineGroupWork,
    title: "Collaboration & Open Source",
    desc: "I enjoy working with teams, building in public, and contributing to the dev community.",
    color: "#10b981",
  },
];

/* ─────────────────────────────────────────
   TECH GROUPS
───────────────────────────────────────── */
const TECH_GROUPS = [
  {
    label: "Languages",
    items: [
      { Icon: SiHtml5,       name: "HTML5",      color: "#e34f26" },
      { Icon: SiCss3,        name: "CSS3",       color: "#1572b6" },
      { Icon: DiJavascript1, name: "JavaScript", color: "#f7df1e" },
      { Icon: SiTypescript,  name: "TypeScript", color: "#3178c6" },
      { Icon: CgCPlusPlus,   name: "C++",        color: "#00599c" },
      { Icon: DiPython,      name: "Python",     color: "#3776ab" },
      { Icon: FaPhp,         name: "PHP",        color: "#8892be" },
    ],
  },
  {
    label: "Frontend",
    items: [
      { Icon: DiReact,       name: "React",       color: "#61dafb" },
      { Icon: SiNextdotjs,   name: "Next.js",     color: "#e2e8f0" },
      { Icon: SiRedux,       name: "Redux",       color: "#764abc" },
      { Icon: SiTailwindcss, name: "Tailwind",    color: "#06b6d4" },
      { Icon: SiBootstrap,   name: "Bootstrap",   color: "#7952b3" },
      { Icon: SiMui,         name: "Material UI", color: "#007fff" },
    ],
  },
  {
    label: "Backend",
    items: [
      { Icon: DiNodejs,        name: "Node.js",    color: "#68a063" },
      { Icon: SiExpress,       name: "Express.js", color: "#e2e8f0" },
      { Icon: SiSocketdotio,   name: "Socket.io",  color: "#94a3b8" },
      { Icon: SiJsonwebtokens, name: "JWT",        color: "#d63aff" },
      { Icon: FaPhp,           name: "PHP",        color: "#8892be" },
    ],
  },
  {
    label: "Databases",
    items: [
      { Icon: DiMongodb,    name: "MongoDB",    color: "#47a248" },
      { Icon: SiPostgresql, name: "PostgreSQL", color: "#336791" },
      { Icon: SiMysql,      name: "MySQL",      color: "#4479a1" },
      { Icon: SiFirebase,   name: "Firebase",   color: "#ffca28" },
    ],
  },
  {
    label: "CS Concepts",
    items: [
      { Icon: TbBinaryTree, name: "DSA",           color: "#f59e0b" },
      { Icon: null, name: "OOP",           color: "#8b5cf6", text: true },
      { Icon: null, name: "SQL",           color: "#4479a1", text: true },
      { Icon: null, name: "REST APIs",     color: "#10b981", text: true },
      { Icon: null, name: "DevOps",        color: "#2496ed", text: true },
      { Icon: null, name: "System Design", color: "#f97316", text: true },
    ],
  },
  {
    label: "DevOps & Cloud",
    items: [
      { Icon: SiDocker,  name: "Docker",  color: "#2496ed" },
      { Icon: DiGit,     name: "Git",     color: "#f05032" },
      { Icon: SiVercel,  name: "Vercel",  color: "#e2e8f0" },
      { Icon: SiNetlify, name: "Netlify", color: "#00c7b7" },
      { Icon: SiRender,  name: "Render",  color: "#46e3b7" },
    ],
  },
  {
    label: "Tools",
    items: [
      { Icon: FaLinux,            name: "Linux",   color: "#fcc624" },
      { Icon: SiVisualstudiocode, name: "VS Code", color: "#007acc" },
      { Icon: SiGithub,           name: "GitHub",  color: "#e2e8f0" },
      { Icon: SiPostman,          name: "Postman", color: "#ff6c37" },
      { Icon: SiNpm,              name: "npm",     color: "#cb3837" },
      { Icon: SiFigma,            name: "Figma",   color: "#f24e1e" },
    ],
  },
];

const CERTIFICATES = [
  { title: "Docker with Hands-on Labs",        issuer: "Coursera",           color: "#2496ed" },
  { title: "Claude 101",                        issuer: "Anthropic",          color: "#d97706" },
  { title: "Learn TypeScript",                  issuer: "Scrimba",            color: "#3178c6" },
  { title: "Project Management",                issuer: "Simplilearn",        color: "#ff6b35" },
  { title: "MongoDB Security",                  issuer: "MongoDB University", color: "#47a248" },
  { title: "CRUD Operations in MongoDB",        issuer: "MongoDB University", color: "#47a248" },
  { title: "Relational Model (SQL) to MongoDB", issuer: "MongoDB University", color: "#47a248" },
  { title: "Learn SQL",                         issuer: "Scrimba",            color: "#4479a1" },
  { title: "Git and GitHub",                    issuer: "Scrimba",            color: "#f05032" },
  { title: "Learn Next.js",                     issuer: "Scrimba",            color: "#e2e8f0" },
  { title: "Tailwind CSS",                      issuer: "Scrimba",            color: "#06b6d4" },
  { title: "React and Redux",                   issuer: "KGCoding",           color: "#61dafb" },
];

/* ─────────────────────────────────────────
   TECH CARD
───────────────────────────────────────── */
function TechCard({ Icon, name, color, text }) {
  const [hovered, setHovered] = useState(false);
  return (
    <>
      {hovered && (
        <div className="tech-hover-label" aria-hidden="true">
          <span className="tech-hover-dot" style={{ background: color }} />
          {name}
        </div>
      )}
      <div
        className="tech-icon-card"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ "--tc": color }}
        aria-label={name}
        title={name}
      >
        <div className="tech-icon-card-icon">
          {text
            ? <span className="tech-text-icon" style={{ color }}>{name.substring(0, 3)}</span>
            : <Icon />}
        </div>
        <span className="tech-icon-card-name">{name}</span>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────
   TECH STACK SECTION
───────────────────────────────────────── */
function TechStackSection() {
  const [active, setActive] = useState(0);
  const group = TECH_GROUPS[active];
  return (
    <div className="about-skills-section">
      <span className="section-label">Skills &amp; Technologies</span>
      <h3 className="about-skills-heading" style={{ marginTop: 12 }}>
        My <span>Tech Stack</span>
      </h3>
      <p className="about-skills-sub">
        Everything I use — from frontend UI to backend APIs, databases, and deployment.
      </p>
      <div className="skills-tabs" role="tablist">
        {TECH_GROUPS.map(({ label }, i) => (
          <button
            key={label}
            role="tab"
            aria-selected={i === active}
            className={`skills-tab${i === active ? " active" : ""}`}
            onClick={() => setActive(i)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="tech-cards-grid" key={group.label}>
        {group.items.map((t) => <TechCard key={t.name} {...t} />)}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   ABOUT SECTION
───────────────────────────────────────── */
function AboutSection() {
  return (
    <section id="about" className="portfolio-section alt">
      <div className="section-container">

        {/* Header */}
        <span className="section-label">About Me</span>
        <h2 className="section-title">Know Who <span>I Am</span></h2>
        <div className="section-divider" />

        {/* Stats */}
        <div className="about-stats-bar">
          {STATS.map(({ num, label, Icon, color }) => (
            <div className="about-stat-card" key={label} style={{ "--sc": color }}>
              <div className="about-stat-icon-wrap">
                <Icon className="about-stat-svg-icon" />
              </div>
              <div className="about-stat-body">
                <span className="about-stat-num">{num}</span>
                <span className="about-stat-label">{label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Profile + bio grid */}
        <div className="about-main-grid">

          {/* LEFT — profile card */}
          <div className="about-profile-card">
            <div className="about-avatar-wrap">
              <div className="about-avatar-ring" />
              <div className="about-avatar">
                <span className="about-avatar-initials">MM</span>
              </div>
              <span className="about-online-badge">● Available</span>
            </div>

            <h3 className="about-profile-name">Muhammad Muaaz</h3>
            <p className="about-profile-role">Full-Stack Developer</p>

            <div className="about-info-pills">
              <span className="about-info-pill">
                <FaMapMarkerAlt /> Lahore, Pakistan
              </span>
              <span className="about-info-pill">
                <FaBriefcase /> Open to Work
              </span>
              <span className="about-info-pill">
                <FaGraduationCap /> Software Engineer
              </span>
            </div>

            <div className="about-profile-socials">
              {[
                { href: "https://github.com/MuhammadMuaaz007",                  Icon: AiFillGithub,     sc: "#e2e8f0", label: "GitHub"   },
                { href: "https://www.linkedin.com/in/muhammad-muaaz-120237297/", Icon: FaLinkedinIn,     sc: "#0a66c2", label: "LinkedIn" },
                { href: "https://x.com/MrMuaaz10/",                             Icon: AiOutlineTwitter, sc: "#1da1f2", label: "Twitter"  },
                { href: "https://leetcode.com/u/Muaaz_/",                       Icon: SiLeetcode,       sc: "#ffa116", label: "LeetCode" },
              ].map(({ href, Icon, sc, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer"
                  aria-label={label} className="about-social-btn" style={{ "--sc": sc }}>
                  <Icon />
                </a>
              ))}
            </div>

            <a
              href="#contact"
              className="about-contact-cta btn-primary-custom"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Get In Touch
            </a>
          </div>

          {/* RIGHT — bio */}
          <div className="about-bio-content">

            <div className="about-intro-block">
              <p className="about-intro-text">
                I'm a <strong className="about-accent">Full-Stack Engineer</strong> who
                loves building things people actually enjoy using. Clean code, thoughtful
                design, and solutions that scale — that's what drives me.
              </p>
              <p className="about-intro-text">
                Lately I've been diving deep into{" "}
                <strong className="about-accent">AI Engineering</strong> — exploring how
                to build intelligent, LLM-powered products and integrate AI into
                real-world applications. It's where I'm headed next.
              </p>
              <p className="about-intro-text">
                I actively <strong className="about-accent">collaborate</strong> on
                projects, contribute to{" "}
                <strong className="about-accent">open source</strong>, and believe the
                best engineers build in public, ship fast, and keep learning.
              </p>
            </div>

            {/* Focus areas */}
            <div className="about-focus-grid">
              {FOCUS_AREAS.map(({ Icon, title, desc, color }) => (
                <div className="about-focus-card" key={title}>
                  <div className="about-focus-icon-wrap" style={{ "--fc": color }}>
                    <Icon className="about-focus-svg-icon" />
                  </div>
                  <div>
                    <p className="about-focus-title">{title}</p>
                    <p className="about-focus-desc">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="about-quote-block">
              "The secret of getting ahead is getting started."
              <div className="about-quote-author">— Mark Twain</div>
            </div>

          </div>
        </div>

        {/* Tech Stack */}
        <TechStackSection />

        {/* Certificates */}
        <div className="about-certs-section">
          <span className="section-label">Credentials</span>
          <h3 className="about-skills-heading" style={{ marginTop: 12 }}>
            Certificates &amp; <span>Badges</span>
          </h3>
          <p className="about-skills-sub">
            Completed courses and certifications across the full-stack ecosystem.
          </p>
          <div className="certs-grid">
            {CERTIFICATES.map(({ title, issuer, color }) => (
              <div key={title} className="cert-card" style={{ "--cert-color": color }}>
                <span className="cert-dot" style={{ background: color }} />
                <div className="cert-body">
                  <p className="cert-title">{title}</p>
                  <span className="cert-issuer-badge">{issuer}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="certs-add-note">
            <FaAward className="certs-add-icon" />
            <span>
              More certifications in progress —{" "}
              <a
                href="https://www.linkedin.com/in/muhammad-muaaz-120237297/"
                target="_blank" rel="noreferrer"
                className="about-inline-link"
              >
                view all on LinkedIn
              </a>
            </span>
          </div>
        </div>

      </div>
    </section>
  );
}

export default AboutSection;
